import { cn } from '@shared/lib';

export type StatusGlyphKind = 'generating' | 'ready' | 'failed' | 'removed';

/**
 * Значок статуса генерации (ТЗ главной, § 15): кольцо с оборотом за 0,8 с, ✓, ! и –.
 * Общий для плашки «Генерация» на `/cards/new`, записи «Генераций» и сигнала у строки.
 * Всегда декоративный (`aria-hidden`): статус рядом подписан словами. ✓ — `success`,
 * а с `muted` — приглушённый (у старых записей нет столбика зелёных галочек); ! — `warn`,
 * – — `muted`. При «меньше движения» кольцо не крутится — статичная дуга.
 */
export function StatusGlyph({
  kind,
  size = 14,
  muted = false,
  className,
}: {
  kind: StatusGlyphKind;
  size?: 12 | 14;
  /** ✓ приглушённым цветом вместо зелёного. */
  muted?: boolean;
  className?: string;
}) {
  const box = size === 12 ? 'h-3 w-3' : 'h-3.5 w-3.5';
  if (kind === 'generating') {
    return (
      <span
        aria-hidden
        className={cn(
          'inline-block shrink-0 animate-[spin_0.8s_linear_infinite] rounded-full motion-reduce:animate-none',
          box,
          className,
        )}
        style={{ border: '2px solid rgba(245,133,63,0.25)', borderTopColor: '#f5853f' }}
      />
    );
  }
  const tone = kind === 'failed' ? 'text-warn' : kind === 'ready' && !muted ? 'text-success' : 'text-muted';
  return (
    <svg
      aria-hidden
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('shrink-0', box, tone, className)}
    >
      {kind === 'ready' && <path d="M3 8.5l3.2 3L13 4.5" />}
      {kind === 'failed' && <path d="M8 4v5M8 11.5v.5" />}
      {kind === 'removed' && <path d="M4.5 8h7" />}
    </svg>
  );
}

/**
 * Блик по строке, пока идёт генерация (`gc-sheen`). Кладётся внутрь контейнера с
 * `relative overflow-hidden`; клики не ловит. При «меньше движения» блика нет.
 */
export function StatusSheen({ className }: { className?: string }) {
  return (
    <div aria-hidden className={cn('pointer-events-none absolute inset-0 overflow-hidden motion-reduce:hidden', className)}>
      <div
        className="animate-gc-sheen absolute inset-y-0 left-0 w-[36%]"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(255,200,150,0.06), transparent)' }}
      />
    </div>
  );
}
